// ensure-cli-latest.ts
//
// Helper chung: detect version hiện tại của 1 CLI phụ thuộc → query latest (npm
// hoặc brew) → lệch + autoUpdate thì cập nhật. Fail-soft mọi nhánh (không throw).
//
// Trừu tượng hoá pattern từ ensure-gitnexus-installed-and-latest.ts để áp cho
// Claude Code (npm), Supabase/git/gitleaks/trivy (brew). Dùng spawnAsyncCapture
// (async, timeout tự kill) — KHÔNG spawnSync.
//
// KHÔNG tự cài mới CLI chưa có (chỉ update cái đã cài) — cài-mới là việc của các
// phase setup riêng (AI/gitnexus). bin không có → "not-installed".

import { spawnAsyncCapture } from "./spawn-async-capture-with-timeout.js";

const VERSION_PROBE_TIMEOUT_MS = 10_000;
const REGISTRY_TIMEOUT_MS = 20_000;
const UPDATE_TIMEOUT_MS = 180_000; // brew/npm upgrade có thể lâu

export type CliManager = "npm" | "brew";

export interface CliSpec {
  name: string; // hiển thị: "Claude Code"
  bin: string; // probe: "claude"
  manager: CliManager;
  pkg: string; // npm pkg "@anthropic-ai/claude-code" | brew formula "gitleaks"
  versionArgs: string[]; // ["--version"] | ["version"]
}

export type CliUpdateAction =
  | "up-to-date"
  | "updated"
  | "update-failed"
  | "skipped-no-manager"
  | "not-installed"
  | "update-available" // chỉ check (autoUpdate=false), có bản mới nhưng chưa cập nhật
  | "binary-broken"; // binary có trên PATH nhưng `--version` lỗi (cài dở)

export interface CliUpdateResult {
  name: string;
  installed: boolean;
  current: string | null;
  latest: string | null;
  action: CliUpdateAction;
  detail?: string;
}

// ── pure parsers (unit-test không cần mock spawn) ───────────────────────────

/** Nuốt mọi dạng: "1.2.3", "v1.2.3", "1.2.3 (Claude Code)", "Version 1.2.3" → "1.2.3". */
export function parseSemver(text: string | null | undefined): string | null {
  if (!text) return null;
  const m = text.match(/(\d+\.\d+\.\d+)/);
  return m ? m[1]! : null;
}

/** `npm view <pkg> version` in version (đôi khi kèm notice) — lấy dòng cuối khớp semver. */
export function parseNpmViewVersion(stdout: string | null | undefined): string | null {
  if (!stdout) return null;
  const last = stdout
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .at(-1);
  return parseSemver(last);
}

/** `brew info --json=v2 <pkg>` → .formulae[0].versions.stable. Fallback: text "stable X.Y.Z". */
export function parseBrewInfoVersion(stdout: string | null | undefined): string | null {
  if (!stdout) return null;
  try {
    const data = JSON.parse(stdout) as {
      formulae?: Array<{ versions?: { stable?: string } }>;
    };
    const stable = data.formulae?.[0]?.versions?.stable;
    if (stable) return parseSemver(stable);
  } catch {
    // không phải JSON → thử text
  }
  const m = stdout.match(/stable\s+(\d+\.\d+\.\d+)/i);
  return m ? m[1]! : parseSemver(stdout);
}

/** Lệnh update theo manager. */
export function buildUpdateArgs(spec: CliSpec): { cmd: string; args: string[] } {
  return spec.manager === "npm"
    ? { cmd: "npm", args: ["install", "-g", `${spec.pkg}@latest`] }
    : { cmd: "brew", args: ["upgrade", spec.pkg] };
}

// ── spawn helpers (fail-soft) ───────────────────────────────────────────────

async function binInstalled(bin: string): Promise<boolean> {
  try {
    const which = process.platform === "win32" ? "where" : "which";
    const r = await spawnAsyncCapture(which, [bin], { timeoutMs: VERSION_PROBE_TIMEOUT_MS });
    return r.status === 0;
  } catch {
    return false;
  }
}

async function managerAvailable(manager: CliManager): Promise<boolean> {
  try {
    const r = await spawnAsyncCapture(manager, ["--version"], {
      timeoutMs: VERSION_PROBE_TIMEOUT_MS,
    });
    return r.status === 0;
  } catch {
    return false;
  }
}

async function currentVersion(spec: CliSpec): Promise<string | null> {
  try {
    const r = await spawnAsyncCapture(spec.bin, spec.versionArgs, {
      timeoutMs: VERSION_PROBE_TIMEOUT_MS,
    });
    if (r.status !== 0) return null;
    return parseSemver(`${r.stdout}\n${r.stderr}`); // 1 số CLI in version ra stderr
  } catch {
    return null;
  }
}

async function latestVersion(spec: CliSpec): Promise<string | null> {
  try {
    if (spec.manager === "npm") {
      const r = await spawnAsyncCapture("npm", ["view", `${spec.pkg}@latest`, "version"], {
        timeoutMs: REGISTRY_TIMEOUT_MS,
      });
      return r.status === 0 ? parseNpmViewVersion(r.stdout) : null;
    }
    const r = await spawnAsyncCapture("brew", ["info", "--json=v2", spec.pkg], {
      timeoutMs: REGISTRY_TIMEOUT_MS,
    });
    return r.status === 0 ? parseBrewInfoVersion(r.stdout) : null;
  } catch {
    return null;
  }
}

async function runUpdate(spec: CliSpec): Promise<boolean> {
  try {
    const { cmd, args } = buildUpdateArgs(spec);
    const r = await spawnAsyncCapture(cmd, args, { timeoutMs: UPDATE_TIMEOUT_MS });
    return r.status === 0;
  } catch {
    return false;
  }
}

// ── orchestrate 1 CLI ───────────────────────────────────────────────────────

export async function ensureCliLatest(
  spec: CliSpec,
  opts: { autoUpdate: boolean },
): Promise<CliUpdateResult> {
  const base = { name: spec.name, installed: false, current: null, latest: null };

  if (!(await binInstalled(spec.bin))) {
    return { ...base, action: "not-installed" };
  }
  if (!(await managerAvailable(spec.manager))) {
    const current = await currentVersion(spec);
    return {
      ...base,
      installed: true,
      current,
      action: "skipped-no-manager",
      detail: `${spec.manager} không có — bỏ qua update`,
    };
  }

  const current = await currentVersion(spec);

  // Tách riêng: current=null nghĩa là binary CÀI DỞ (which thành công nhưng --version lỗi).
  // KHÔNG gộp với "offline" (!latest) vì hành vi + thông báo khác nhau hoàn toàn.
  if (current === null) {
    if (opts.autoUpdate) {
      // Thử reinstall để khôi phục binary hỏng
      const reinstallOk = await runUpdate(spec);
      if (reinstallOk) {
        const after = await currentVersion(spec);
        if (after !== null) {
          // Reinstall thành công, binary đã phục hồi
          return {
            name: spec.name,
            installed: true,
            current: after,
            latest: null,
            action: "updated",
            detail: "reinstall do binary hỏng",
          };
        }
      }
    }
    // autoUpdate=false hoặc reinstall không khôi phục được → báo binary-broken
    const { cmd, args } = buildUpdateArgs(spec);
    return {
      name: spec.name,
      installed: true,
      current: null,
      latest: null,
      action: "binary-broken",
      detail: `${spec.name} cài dở, \`${spec.bin} ${spec.versionArgs.join(" ")}\` lỗi; reinstall không khôi phục — chạy thủ công: ${cmd} ${args.join(" ")}`,
    };
  }

  const latest = await latestVersion(spec);

  // Không lấy được latest (offline/format lạ) hoặc đã mới nhất → coi như up-to-date, fail-soft.
  if (!latest || current === latest) {
    return { name: spec.name, installed: true, current, latest, action: "up-to-date" };
  }

  if (!opts.autoUpdate) {
    return { name: spec.name, installed: true, current, latest, action: "update-available" };
  }

  const ok = await runUpdate(spec);
  if (ok) {
    const after = await currentVersion(spec);
    return { name: spec.name, installed: true, current: after, latest, action: "updated" };
  }
  return {
    name: spec.name,
    installed: true,
    current,
    latest,
    action: "update-failed",
    detail: `Không cập nhật được (dùng bản cũ v${current})`,
  };
}
